<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

interface CountrySite {
  code: string
  name: string
  url: string
}

const props = defineProps<{
  countrySites: CountrySite[] | string
}>()

const { t } = useI18n()
const isOpen = ref(false)
const selectedSite = ref<CountrySite | null>(null)
const parsedSites = ref<CountrySite[]>([])
const dropdownRef = ref<HTMLElement | null>(null)

// Country code to CSS class mapping
const getFlagClass = (countryCode: string): string => {
  return `flag flag-${countryCode.toLowerCase()}`
}

// Toggle dropdown
function toggleDropdown() {
  isOpen.value = !isOpen.value
}

// Select country and redirect
function selectCountry(site: CountrySite) {
  selectedSite.value = site
  isOpen.value = false
  if (site?.url) {
    window.location.href = site.url
  }
}

// Close dropdown when clicking outside
function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

// Parse country sites from prop (handles both string and array)
watch(() => props.countrySites, (newVal) => {
  if (typeof newVal === 'string') {
    try {
      parsedSites.value = JSON.parse(newVal)
    } catch (e) {
      console.error('Failed to parse countrySites JSON', e)
      return
    }
  } else {
    parsedSites.value = newVal
  }

  // Set initial selection to current URL match or first country
  const currentUrl = window.location.href
  const matched = parsedSites.value.find(site => currentUrl.startsWith(site.url))
  selectedSite.value = matched || parsedSites.value[0] || null
}, { immediate: true })

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="country-picker" ref="dropdownRef">
    <div class="flag-trigger" @click="toggleDropdown">
      <span :class="selectedSite ? getFlagClass(selectedSite.code) : 'flag flag-default'"></span>
    </div>

    <div v-if="isOpen" class="dropdown-menu">
      <div
        v-for="site in parsedSites"
        :key="site.code"
        class="dropdown-item"
        :class="{ 'selected': selectedSite?.code === site.code }"
        @click="selectCountry(site)"
      >
        <span :class="getFlagClass(site.code)"></span>
        <span class="name">{{ site.name }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.country-picker {
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.flag-trigger {
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  display: inline-block;
  padding: 4px;
  border-radius: $radius-sm;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  left: auto;
  background: white;
  border: 1px solid #ddd;
  border-radius: $radius-sm;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  min-width: 150px;

  /* Mobile: picker sits on the left of the header — anchor to left instead */
  @media (max-width: 991.98px) {
    right: auto;
    left: 0;
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  line-height: 1.4;

  &:hover {
    background: #f5f5f5;
  }

  &.selected {
    background: #e3f2fd;
    font-weight: bold;
  }

  &:last-child {
    border-bottom: none;
  }
}

.flag {
  display: inline-block;
  width: 24px;
  height: 16px;
  margin-right: 8px;
  background-size: cover;
  background-position: center;
  border-radius: $radius-xs;

  &.flag-default {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ddd"/><text x="12" y="12" text-anchor="middle" font-size="10" fill="%23999">🌍</text></svg>');
  }

  &.flag-at {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%23ed2939"/><rect y="5.33" width="24" height="5.33" fill="white"/><rect y="10.67" width="24" height="5.33" fill="%23ed2939"/></svg>');
  }

  &.flag-de {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%23000"/><rect y="5.33" width="24" height="5.33" fill="%23dd0000"/><rect y="10.67" width="24" height="5.33" fill="%23ffce00"/></svg>');
  }

  &.flag-fr {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%230023a8"/><rect x="8" width="8" height="16" fill="white"/><rect x="16" width="8" height="16" fill="%23ed2939"/></svg>');
  }

  &.flag-ch {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ff0000"/><rect x="9" y="6" width="6" height="4" fill="white"/><rect x="10" y="3" width="4" height="10" fill="white"/></svg>');
  }

  &.flag-us {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23b22234"/><rect x="0" y="1.23" width="24" height="1.23" fill="white"/><rect x="0" y="3.69" width="24" height="1.23" fill="white"/><rect x="0" y="6.15" width="24" height="1.23" fill="white"/><rect x="0" y="8.62" width="24" height="1.23" fill="white"/><rect x="0" y="11.08" width="24" height="1.23" fill="white"/><rect x="0" y="13.54" width="24" height="1.23" fill="white"/><rect x="0" y="0" width="9.6" height="8.62" fill="%233c3b6e"/></svg>');
  }

  &.flag-ca {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ff0000"/><rect x="6" y="0" width="12" height="16" fill="white"/><polygon points="12,2 14,8 20,8 15,12 17,18 12,14 7,18 9,12 4,8 10,8" fill="%23ff0000"/></svg>');
  }

  &.flag-nl {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ae1c28"/><rect y="5.33" width="24" height="5.33" fill="white"/><rect y="10.67" width="24" height="5.33" fill="%2321468b"/></svg>');
  }

  &.flag-be {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%23000"/><rect x="8" width="8" height="16" fill="%23ffd700"/><rect x="16" width="8" height="16" fill="%23ed2939"/></svg>');
  }

  &.flag-lu {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%2300a5e6"/><rect x="8" width="8" height="16" fill="white"/><rect x="16" width="8" height="16" fill="%23ed2939"/></svg>');
  }

  &.flag-it {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%2300a651"/><rect x="8" width="8" height="16" fill="white"/><rect x="16" width="8" height="16" fill="%23ed2939"/></svg>');
  }

  &.flag-es {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23aa151b"/><rect y="5.33" width="24" height="5.33" fill="%23f1bf00"/></svg>');
  }

  &.flag-gb {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23012169"/><path d="M12,0 L12,16 M0,8 L24,8" stroke="white" stroke-width="2.67"/><path d="M12,0 L12,16 M0,8 L24,8" stroke="%23C8102E" stroke-width="1.33"/></svg>');
  }

  &.flag-ie {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%23009a49"/><rect x="8" width="8" height="16" fill="white"/><rect x="16" width="8" height="16" fill="%23ff7900"/></svg>');
  }

  &.flag-dk {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23c60c30"/><rect x="6" y="0" width="2" height="16" fill="white"/></svg>');
  }

  &.flag-no {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ba0c2f"/><rect x="6" y="0" width="2" height="16" fill="white"/><rect x="0" y="6.4" width="24" height="2.67" fill="white"/><rect x="6" y="6.4" width="2" height="2.67" fill="%2300205b"/></svg>');
  }

  &.flag-se {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%230060aa"/><rect x="6" y="0" width="2" height="16" fill="%23fecc00"/></svg>');
  }

  &.flag-fi {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="white"/><rect x="6" y="0" width="2" height="16" fill="%23003380"/><rect x="0" y="6.4" width="24" height="2.67" fill="%23003380"/></svg>');
  }

  &.flag-pl {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="8" fill="white"/><rect y="8" width="24" height="8" fill="%23dc143c"/></svg>');
  }

  &.flag-cz {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="8" fill="white"/><rect y="8" width="24" height="8" fill="%23d7141a"/><path d="M0,0 L12,8 L0,16" fill="%2311447e"/></svg>');
  }

  &.flag-sk {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="8" fill="%23ee1c25"/><rect y="8" width="24" height="8" fill="white"/><path d="M0,0 L12,8 L0,16" fill="%230b5394"/></svg>');
  }

  &.flag-hu {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%23ce2930"/><rect y="5.33" width="24" height="5.33" fill="white"/><rect y="10.67" width="24" height="5.33" fill="%2343729c"/></svg>');
  }

  &.flag-ro {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%2300258c"/><rect x="8" width="8" height="16" fill="%23ffd700"/><rect x="16" width="8" height="16" fill="%23ce2930"/></svg>');
  }

  &.flag-bg {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="white"/><rect y="5.33" width="24" height="5.33" fill="%2300966e"/><rect y="10.67" width="24" height="5.33" fill="%23d62612"/></svg>');
  }

  &.flag-hr {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ff0000"/><rect x="6" y="0" width="12" height="16" fill="white"/><rect x="8" y="0" width="8" height="16" fill="%2300198c"/><path d="M12,3 L14,7 L18,7 L15,10 L16,14 L12,11 L8,14 L9,10 L6,7 L10,7" fill="%23ffd700"/></svg>');
  }

  &.flag-si {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="white"/><rect x="6" y="0" width="12" height="16" fill="%2300529c"/><path d="M12,4 L13.5,8 L18,8 L14.5,11 L16,15 L12,12 L8,15 L9.5,11 L6,8 L10.5,8" fill="%23ffd700"/></svg>');
  }

  &.flag-ee {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%230072ce"/><rect y="5.33" width="24" height="5.33" fill="black"/><rect y="10.67" width="24" height="5.33" fill="white"/></svg>');
  }

  &.flag-lv {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%239e1b34"/><rect y="5.33" width="24" height="5.33" fill="white"/><rect y="10.67" width="24" height="5.33" fill="%239e1b34"/></svg>');
  }

  &.flag-lt {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="5.33" fill="%23fdb913"/><rect y="5.33" width="24" height="5.33" fill="%230027a6"/><rect y="10.67" width="24" height="5.33" fill="%23c1272d"/></svg>');
  }

  &.flag-gr {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%230c5ab4"/><rect x="0" y="0" width="10.67" height="8" fill="white"/><rect x="0" y="8" width="10.67" height="8" fill="white"/><rect x="10.67" y="0" width="2.67" height="16" fill="white"/><rect x="0" y="5.33" width="24" height="2.67" fill="white"/></svg>');
  }

  &.flag-pt {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="8" height="16" fill="%2300608d"/><rect x="8" width="16" height="16" fill="%23ffcc00"/><rect x="13.33" y="0" width="10.67" height="16" fill="%23ff0000"/></svg>');
  }

  &.flag-cy {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="white"/><rect x="0" y="0" width="24" height="16" fill="%23f5f5f5"/><rect x="0" y="0" width="8" height="16" fill="%23f5f5f5"/><rect x="8" y="0" width="8" height="16" fill="white"/><rect x="16" y="0" width="8" height="16" fill="%23ff7900"/></svg>');
  }

  &.flag-mt {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="white"/><rect x="0" y="0" width="8" height="16" fill="%23cf142b"/><rect x="16" y="0" width="8" height="16" fill="%23cf142b"/></svg>');
  }

  &.flag-au {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23012169"/><rect x="0" y="0" width="9.6" height="8.62" fill="%23f5f5f5"/></svg>');
  }

  &.flag-nz {
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23012169"/><rect x="0" y="0" width="9.6" height="8.62" fill="%23f5f5f5"/></svg>');
  }
}

.name {
  flex: 1;
}
</style>
